import styled from 'styled-components';

import Tag from 'components/shared/Tag';
import Tooltip from 'components/shared/Tooltip';
import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';
import TrashFilled from 'svg/TrashFilled';

type Props = {
  filename: string;
  onRemove: () => void;
};

/**
 * Render-only attachment row used to preview the auto-included CCD on a
 * referral. Visually mirrors `ExternalAttachment` so it sits among real
 * attachments without standing out. The CCD doc-type chip is non-editable;
 * the trash icon unchecks the "Include CCD" checkbox upstream, which is
 * what actually hides this row.
 */
export default function CcdPlaceholder({ filename, onRemove }: Props) {
  return (
    <AttachmentContainer data-testid='ccd-placeholder'>
      <AttachmentInformation>
        <Filename>{filename}</Filename>
        <Tag label='CCD' onClear={onRemove} />
        <Tooltip text='Remove Attachment'>
          <TrashFilled
            onClick={onRemove}
            color={colors.black100}
            fill={colors.black100}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </AttachmentInformation>
    </AttachmentContainer>
  );
}

const AttachmentContainer = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.black3};
  border: 0.5px solid ${({ theme }) => theme.colors.black10};
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  padding: 4px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 32px;
`;

const AttachmentInformation = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;

  > :first-child {
    flex: 1;
    width: 0;
  }
`;

const Filename = styled(BodySmallBold)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: 500;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;
